import {
  ArrowDown,
  ArrowLeft,
  ArrowUp,
  Award,
  Check,
  Copy,
  Loader2,
  Plus,
  Save,
  Trash2,
  X,
} from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { ROUTES } from '../../constants/routes';
import {
  useBeltProfile,
  useBeltProfileRankReferences,
} from '../../hooks/belts/useBelts';
import {
  useCopyCriteria,
  useDeleteBeltProfile,
  useSaveRanks,
  useUpdateBeltProfile,
} from '../../hooks/belts/useBeltMutations';
import type { BeltRank, RankDraft } from '../../types/belts';

/**
 * Edit a company-owned belt profile (#746).
 *
 * Loads the profile + its ranks, stages all edits client-side, then saves
 * everything in one shot (useSaveRanks performs a careful two-phase reorder
 * to avoid colliding with UNIQUE (profile_id, rank_order)). System templates
 * route here too if linked directly, but RLS blocks writes — we surface a
 * read-only banner in that case.
 */
export default function BeltProfileEditor() {
  const { profileId } = useParams<{ profileId: string }>();
  const navigate = useNavigate();

  const profileQuery = useBeltProfile(profileId);
  const refsQuery = useBeltProfileRankReferences(profileId);
  const updateProfile = useUpdateBeltProfile();
  const saveRanks = useSaveRanks();
  const deleteProfile = useDeleteBeltProfile();
  const copyCriteria = useCopyCriteria();

  // Staged form state. Hydrated from server data on load; cleared from "dirty"
  // when a save round-trips.
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [drafts, setDrafts] = useState<RankDraft[]>([]);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [copySourceRank, setCopySourceRank] = useState<RankDraft | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Hydrate staged state when the server query resolves.
  useEffect(() => {
    if (!profileQuery.data) return;
    setName(profileQuery.data.name);
    setDescription(profileQuery.data.description ?? '');
    setDrafts(
      profileQuery.data.belt_ranks.map((r) => ({
        id: r.id,
        name: r.name,
        rank_order: r.rank_order,
        time_required_days: r.time_required_days,
        attendance_required: r.attendance_required,
      }))
    );
  }, [profileQuery.data]);

  const existingIds = useMemo(
    () => (profileQuery.data?.belt_ranks ?? []).map((r: BeltRank) => r.id),
    [profileQuery.data]
  );

  const isReadOnly = profileQuery.data?.is_system === true;
  const isLoading = profileQuery.isLoading;
  const isSaving = updateProfile.isPending || saveRanks.isPending;

  if (isLoading) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <div className="flex items-center gap-2 text-white/60">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading profile…
        </div>
      </div>
    );
  }

  if (!profileQuery.data) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <Link
          to={ROUTES.BELTS}
          className="inline-flex items-center gap-1 text-sm text-white/60 hover:text-white"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>
        <div className="mt-4 text-white/70">Profile not found.</div>
      </div>
    );
  }

  const moveDraft = (idx: number, dir: -1 | 1) => {
    const next = [...drafts];
    const swap = idx + dir;
    if (swap < 0 || swap >= next.length) return;
    [next[idx], next[swap]] = [next[swap], next[idx]];
    setDrafts(next.map((d, i) => ({ ...d, rank_order: i })));
  };

  const updateDraft = (idx: number, patch: Partial<RankDraft>) => {
    setDrafts((prev) =>
      prev.map((d, i) => (i === idx ? { ...d, ...patch } : d))
    );
  };

  const removeDraft = (idx: number) => {
    setDrafts((prev) =>
      prev.filter((_, i) => i !== idx).map((d, i) => ({ ...d, rank_order: i }))
    );
  };

  const addDraft = () => {
    setDrafts((prev) => [
      ...prev,
      {
        name: 'New rank',
        rank_order: prev.length,
        time_required_days: null,
        attendance_required: null,
      },
    ]);
  };

  const save = async () => {
    if (!profileId) return;
    setSaveError(null);
    try {
      const profile = profileQuery.data!;
      const profileChanged =
        name !== profile.name || description !== (profile.description ?? '');
      if (profileChanged) {
        await updateProfile.mutateAsync({
          profileId,
          name,
          description: description.trim() === '' ? null : description,
        });
      }
      await saveRanks.mutateAsync({
        profileId,
        drafts,
        existingIds,
      });
    } catch (e) {
      setSaveError((e as Error).message);
    }
  };

  const onDelete = async () => {
    if (!profileId) return;
    try {
      await deleteProfile.mutateAsync(profileId);
      navigate(ROUTES.BELTS);
    } catch (e) {
      setSaveError((e as Error).message);
      setConfirmDelete(false);
    }
  };

  const onCopyCriteria = async (targetIds: string[]) => {
    if (!profileId || !copySourceRank) return;
    try {
      await copyCriteria.mutateAsync({
        profileId,
        source: {
          time_required_days: copySourceRank.time_required_days,
          attendance_required: copySourceRank.attendance_required,
        },
        targetRankIds: targetIds,
      });
      setCopySourceRank(null);
    } catch (e) {
      setSaveError((e as Error).message);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6">
      <div>
        <Link
          to={ROUTES.BELTS}
          className="inline-flex items-center gap-1 text-sm text-white/60 hover:text-white"
        >
          <ArrowLeft className="w-4 h-4" /> Back to profiles
        </Link>
      </div>

      <div className="flex items-start gap-3">
        <Award className="w-6 h-6 text-emerald-400 mt-1" />
        <div className="flex-1 space-y-2">
          <input
            type="text"
            value={name}
            disabled={isReadOnly}
            onChange={(e) => setName(e.target.value)}
            className="w-full bg-transparent text-2xl font-bold text-[#EBFFEE] focus:outline-none disabled:opacity-70"
          />
          <textarea
            value={description}
            disabled={isReadOnly}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Description (optional)"
            rows={2}
            className="w-full bg-transparent text-sm text-white/70 placeholder-white/30 resize-none focus:outline-none disabled:opacity-70"
          />
        </div>
      </div>

      {isReadOnly && (
        <div className="rounded-md bg-amber-500/10 border border-amber-500/30 text-amber-200 p-3 text-sm">
          This is a system template &mdash; it&rsquo;s read-only. Clone it from
          the profiles list to make an editable copy.
        </div>
      )}

      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-[#EBFFEE]">Ranks</h2>
          <span className="text-xs text-white/50">
            {drafts.length} rank{drafts.length === 1 ? '' : 's'} ·
            thresholds advisory only
          </span>
        </div>

        <div className="rounded-lg border border-white/10 overflow-hidden">
          <div className="grid grid-cols-[auto_1fr_120px_120px_auto_auto] gap-2 px-3 py-2 bg-white/[0.04] text-xs uppercase tracking-wide text-white/50">
            <span>#</span>
            <span>Name</span>
            <span>Days</span>
            <span>Classes</span>
            <span></span>
            <span></span>
          </div>
          {drafts.map((d, idx) => (
            <RankRowEditor
              key={d.id ?? `new-${idx}`}
              draft={d}
              index={idx}
              isReadOnly={isReadOnly}
              canMoveUp={idx > 0}
              canMoveDown={idx < drafts.length - 1}
              onMove={(dir) => moveDraft(idx, dir)}
              onChange={(patch) => updateDraft(idx, patch)}
              onRemove={() => removeDraft(idx)}
              onCopyFrom={() => setCopySourceRank(d)}
            />
          ))}
        </div>

        {!isReadOnly && (
          <button
            type="button"
            onClick={addDraft}
            className="inline-flex items-center gap-1.5 rounded-md bg-white/5 hover:bg-white/10 text-white/80 px-3 py-1.5 text-sm border border-white/10"
          >
            <Plus className="w-4 h-4" /> Add rank
          </button>
        )}
      </section>

      {saveError && (
        <div className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-md p-3">
          {saveError}
        </div>
      )}

      {!isReadOnly && (
        <div className="flex items-center justify-between border-t border-white/10 pt-4">
          <button
            type="button"
            onClick={() => setConfirmDelete(true)}
            className="inline-flex items-center gap-1.5 text-sm text-red-300/80 hover:text-red-200"
          >
            <Trash2 className="w-4 h-4" /> Delete profile
          </button>
          <button
            type="button"
            onClick={save}
            disabled={isSaving}
            className="inline-flex items-center gap-1.5 rounded-md bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 text-black font-medium px-4 py-2 text-sm"
          >
            {isSaving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Save changes
          </button>
        </div>
      )}

      {copySourceRank && (
        <CopyCriteriaDialog
          source={copySourceRank}
          drafts={drafts}
          onClose={() => setCopySourceRank(null)}
          onApply={onCopyCriteria}
          isPending={copyCriteria.isPending}
        />
      )}

      {confirmDelete && (
        <DeleteConfirmDialog
          profileName={name}
          referencingStudents={refsQuery.data ?? 0}
          isLoadingRefs={refsQuery.isLoading}
          isPending={deleteProfile.isPending}
          onCancel={() => setConfirmDelete(false)}
          onConfirm={onDelete}
        />
      )}
    </div>
  );
}

function RankRowEditor({
  draft,
  index,
  isReadOnly,
  canMoveUp,
  canMoveDown,
  onMove,
  onChange,
  onRemove,
  onCopyFrom,
}: {
  draft: RankDraft;
  index: number;
  isReadOnly: boolean;
  canMoveUp: boolean;
  canMoveDown: boolean;
  onMove: (dir: -1 | 1) => void;
  onChange: (patch: Partial<RankDraft>) => void;
  onRemove: () => void;
  onCopyFrom: () => void;
}) {
  return (
    <div className="grid grid-cols-[auto_1fr_120px_120px_auto_auto] gap-2 items-center px-3 py-2 border-t border-white/5 hover:bg-white/[0.02]">
      <span className="text-xs text-white/40 w-6">{index + 1}</span>
      <input
        type="text"
        value={draft.name}
        disabled={isReadOnly}
        onChange={(e) => onChange({ name: e.target.value })}
        className="bg-transparent text-sm text-[#EBFFEE] focus:outline-none border-b border-transparent focus:border-white/20"
      />
      <input
        type="number"
        min={0}
        value={draft.time_required_days ?? ''}
        disabled={isReadOnly}
        placeholder="—"
        onChange={(e) =>
          onChange({
            time_required_days:
              e.target.value === '' ? null : Math.max(0, Number(e.target.value)),
          })
        }
        className="bg-white/5 border border-white/10 rounded px-2 py-1 text-sm text-[#EBFFEE] focus:outline-none focus:border-emerald-500/50 disabled:opacity-50"
      />
      <input
        type="number"
        min={0}
        value={draft.attendance_required ?? ''}
        disabled={isReadOnly}
        placeholder="—"
        onChange={(e) =>
          onChange({
            attendance_required:
              e.target.value === '' ? null : Math.max(0, Number(e.target.value)),
          })
        }
        className="bg-white/5 border border-white/10 rounded px-2 py-1 text-sm text-[#EBFFEE] focus:outline-none focus:border-emerald-500/50 disabled:opacity-50"
      />
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onMove(-1)}
          disabled={isReadOnly || !canMoveUp}
          title="Move up"
          className="text-white/40 hover:text-white/80 disabled:opacity-20 disabled:cursor-not-allowed p-1"
        >
          <ArrowUp className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => onMove(1)}
          disabled={isReadOnly || !canMoveDown}
          title="Move down"
          className="text-white/40 hover:text-white/80 disabled:opacity-20 disabled:cursor-not-allowed p-1"
        >
          <ArrowDown className="w-4 h-4" />
        </button>
      </div>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={onCopyFrom}
          disabled={isReadOnly}
          title="Copy these thresholds to other ranks"
          className="text-white/40 hover:text-emerald-300 disabled:opacity-20 p-1"
        >
          <Copy className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={onRemove}
          disabled={isReadOnly}
          title="Remove rank"
          className="text-white/40 hover:text-red-300 disabled:opacity-20 p-1"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function CopyCriteriaDialog({
  source,
  drafts,
  onClose,
  onApply,
  isPending,
}: {
  source: RankDraft;
  drafts: RankDraft[];
  onClose: () => void;
  onApply: (targetIds: string[]) => void;
  isPending: boolean;
}) {
  // Only existing (persisted) ranks can be targets — newly-added draft rows
  // don't have an id yet so there's nothing to bulk-UPDATE. The user can
  // save first, then re-open the copy dialog.
  const candidates = drafts.filter((d) => d.id && d !== source) as Array<
    RankDraft & { id: string }
  >;
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-[#0f1612] border border-white/10 rounded-xl shadow-2xl w-full max-w-md p-6 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-[#EBFFEE]">
              Copy criteria
            </h3>
            <p className="text-xs text-white/60 mt-1">
              Apply{' '}
              <span className="text-[#EBFFEE]">
                {source.time_required_days ?? '—'} days
              </span>{' '}
              and{' '}
              <span className="text-[#EBFFEE]">
                {source.attendance_required ?? '—'} classes
              </span>{' '}
              from <span className="text-[#EBFFEE]">{source.name}</span> to
              other ranks.
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-white/50 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        {candidates.length === 0 ? (
          <p className="text-sm text-white/60">
            No other saved ranks to copy to. Save your changes first to enable
            this for newly-added ranks.
          </p>
        ) : (
          <div className="max-h-72 overflow-y-auto rounded-md border border-white/10 divide-y divide-white/5">
            {candidates.map((c) => (
              <label
                key={c.id}
                className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-white/[0.04]"
              >
                <input
                  type="checkbox"
                  checked={selected.has(c.id)}
                  onChange={() => toggle(c.id)}
                  className="accent-emerald-500"
                />
                <span className="text-sm text-[#EBFFEE] flex-1">{c.name}</span>
                <span className="text-xs text-white/40">
                  {c.time_required_days ?? '—'}d ·{' '}
                  {c.attendance_required ?? '—'}c
                </span>
              </label>
            ))}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-sm text-white/70 hover:text-white"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onApply(Array.from(selected))}
            disabled={isPending || selected.size === 0}
            className="inline-flex items-center gap-1.5 rounded-md bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 text-black px-3 py-1.5 text-sm font-medium"
          >
            {isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Check className="w-4 h-4" />
            )}
            Apply to {selected.size}
          </button>
        </div>
      </div>
    </div>
  );
}

function DeleteConfirmDialog({
  profileName,
  referencingStudents,
  isLoadingRefs,
  isPending,
  onCancel,
  onConfirm,
}: {
  profileName: string;
  referencingStudents: number;
  isLoadingRefs: boolean;
  isPending: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const blocked = referencingStudents > 0;
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-[#0f1612] border border-red-500/20 rounded-xl shadow-2xl w-full max-w-md p-6 space-y-4">
        <h3 className="text-lg font-semibold text-red-200">Delete profile?</h3>
        <p className="text-sm text-white/70">
          This will permanently delete &ldquo;{profileName}&rdquo; and all of
          its ranks. Cannot be undone.
        </p>

        {isLoadingRefs ? (
          <div className="text-xs text-white/50">Checking student references…</div>
        ) : blocked ? (
          <div className="rounded-md bg-red-500/10 border border-red-500/30 text-red-200 text-sm p-3">
            <strong>{referencingStudents}</strong> student
            {referencingStudents === 1 ? ' is' : 's are'} currently ranked using
            this profile. Move them to another profile first, then try again.
          </div>
        ) : (
          <div className="rounded-md bg-white/5 border border-white/10 text-white/70 text-sm p-3">
            No students are currently using this profile&rsquo;s ranks. Safe to
            delete.
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-3 py-1.5 text-sm text-white/70 hover:text-white"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={isPending || blocked}
            className="inline-flex items-center gap-1.5 rounded-md bg-red-500 hover:bg-red-400 disabled:opacity-40 disabled:cursor-not-allowed text-white px-3 py-1.5 text-sm font-medium"
          >
            {isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            <Trash2 className="w-4 h-4" /> Delete
          </button>
        </div>
      </div>
    </div>
  );
}
